import React = require('react');
import ColorUtil from 'diy/util/color';
import TextField from  'material-ui/TextField';
import l10n = require('diy/util/l10n');
import {autoprefixer} from "diy/material-ui/autoprefixer";
import {ColorSwatchItem} from './ColorSwatchItem';

import ArrowUpIcon from 'material-ui/svg-icons/navigation/arrow-drop-up';
import ArrowDownIcon from 'material-ui/svg-icons/navigation/arrow-drop-down';

import {ThemingZones} from './ThemingZones';

import {BackgroundType} from "../../state";

export interface ColorRgba {
    red: number;
    green: number;
    blue: number;
    alpha: number;
}

export enum ColorType {
    Swatch,
    Theming,
    Custom
}

export interface ColorSelectorViewProps extends React.Props<ColorSelectorView> {
    type: BackgroundType;
    color: ColorRgba;
    themingZone: number;
    showThemingZones: boolean;
    onColorSelected: (color: ColorRgba, category: ColorType) => void;
    onThemingZoneSelected: (zone: number) => void;
}
interface ColorSelectorViewState {
    customColorOpened: boolean;
    customColorValue: string;
    customColorInputValue: string;
    customColorValidationErrors: string[];
}

export default class ColorSelectorView extends React.Component<ColorSelectorViewProps, ColorSelectorViewState> {
    private defaultCustomColor = '';

    private predefinedColors: string[] = [
        '#b81c1c', '#32691e', '#00579a', '#f57f17', '#455a64', '#000000',
        '#f44336', '#61994d', '#2985cc', '#fbc02d', '#607d8b', '#c7c7c7',
        '#e67373', '#b5e6a3', '#66bdff', '#fff066', '#90a4ae', '#a9a8a8',
        '#ffe5e5', '#ecffe5', '#cce9ff', '#fffbd9', '#cfd8dc', '#ffffff'
    ];

    constructor(props: ColorSelectorViewProps) {
        super(props);

        let hexValue = props.color ? ColorUtil.rgb2hex(props.color) : this.defaultCustomColor;

        this.state = {
            customColorOpened: props.type === BackgroundType.Color && this.predefinedColors.indexOf(hexValue) === -1,
            customColorValue: hexValue,
            customColorInputValue: hexValue,
            customColorValidationErrors: []
        };
    }

    onColorSelected(colorHexValue: string, source: ColorType, force = false) {
        //used the hex comparison because of the "number"<>number comparison fail
        let prevColor = this.props.color;
        if (force || !prevColor ||
            (prevColor && colorHexValue !== ColorUtil.rgb2hex(prevColor))
        ) {
            let rgbaValue: ColorRgba = ColorUtil.hex2rgb(colorHexValue) as ColorRgba;
            rgbaValue.alpha = 1;

            this.props.onColorSelected(rgbaValue, source);
        }
    }

    getGroupedPredefinedColors(itemsPerLine = 6) {
        let noLines = this.predefinedColors.length / itemsPerLine;
        let groupedColors: [string[]] = [[]];

        for (let i = 0; i < noLines; i++) {
            let start = itemsPerLine * i;
            groupedColors[i] = this.predefinedColors.slice(start, start + itemsPerLine);
        }

        return groupedColors;
    }

    protected onChangeCustomColor(value: string, force = false) {
        let correctedValue = value;
        let newState: ColorSelectorViewState = {
            customColorValidationErrors: []
        } as ColorSelectorViewState;

        if (value.substr(0, 1) === '#') {
            correctedValue = correctedValue.substr(1, correctedValue.length - 1);
        }/* else {
            newState.customColorValidationErrors.push("A valid hex color begins with #");
        }*/

        if (correctedValue.length > 6) {
            correctedValue = correctedValue.substr(0, 6);
            //no error showed - just emulate a maxLength
        }

        let i = 0;
        let invalidCharsFound = false;
        while (i < correctedValue.length) {
            let char = correctedValue[i];
            if (!char.match(/[a-f0-9]/)) {
                correctedValue = correctedValue.replace(char, '');
                invalidCharsFound = true;
            }
            i++;
        }

        if (invalidCharsFound) {
            newState.customColorValidationErrors.push(
                l10n.getString('section.colorSelector.customColor.validation.invalidCharacters')
            );
        }

        correctedValue = '#' + correctedValue;

        newState.customColorInputValue = correctedValue;
        this.setState(newState);

        if (correctedValue.length === 7) {
            this.onColorSelected(correctedValue, ColorType.Custom, force);
        }
    }

    renderCustomInput() {
        let hexPropColorValue = this.props.color ? ColorUtil.rgb2hex(this.props.color) : this.defaultCustomColor;
        let isCustomColorInputUsed =
            this.props.type === BackgroundType.Color &&
            hexPropColorValue === this.state.customColorInputValue
            && this.predefinedColors.indexOf(hexPropColorValue) === -1;

        return <div style={{
            backgroundColor: '#f2f2f2',
            minHeight: 20,
            color: 'lightgrey',
            padding: 10
        }}>
            <div
                style={autoprefixer({
                    display: 'flex',
                    flexDirection: 'row',
                    cursor: 'pointer',
                    alignItems: 'center'
                })}
                onClick={() => {
                    this.setState({
                        customColorOpened: !this.state.customColorOpened,
                        customColorValue: hexPropColorValue,
                        customColorInputValue: hexPropColorValue
                    } as ColorSelectorViewState);
                }}
                data-test-id="custom-color-toggle"
            >
                <div
                    style={autoprefixer({
                        flexGrow: 1,
                        fontSize: 14,
                        color: '#666',
                        display: 'flex',
                        alignItems: 'center'
                    })}
                >
                    {l10n.getString('section.colorSelector.customColor.title')}
                    {this.state.customColorOpened ? <ArrowUpIcon /> : <ArrowDownIcon />}
                </div>
                <div
                    style={Object.assign(
                        autoprefixer({flexGrow: 0}),
                        this.state.customColorOpened || isCustomColorInputUsed ?
                            {} :
                            {visibility: 'hidden'}/* this keeps the height of the custom color expand box fixed */
                    )}
                >
                <ColorSwatchItem
                    color={this.state.customColorInputValue}
                    checked={isCustomColorInputUsed}
                />
                </div>
            </div>
            {this.state.customColorOpened ?
                <TextField
                    errorText={this.state.customColorValidationErrors.join("<br />")}
                    value={this.state.customColorInputValue}
                    floatingLabelText={l10n.getString('section.colorSelector.customColor.inputField.floatingLabelText')}
                    onChange={e => {
                        this.onChangeCustomColor((e.target as HTMLInputElement).value);
                    }}
                    onFocus={e => {
                        this.onChangeCustomColor((e.target as HTMLInputElement).value, true);
                    }}
                    style={{maxWidth: 218}}
                    data-test-id="custom-color-input"
                /> : null
            }
        </div>;
    }

    render() {
        let itemsPerLine = 6;

        return <div>
            <div
                style={{
                    padding: '0 10px 10px 10px',
                    /*put the padding here so we could align the custom color box to the popover width, no spacing*/
                }}
            >
                {this.props.showThemingZones ? <div
                    style={{
                        fontFamily: "open sans",
                        fontSize: '14px',
                        lineHeight: '14px',
                        paddingBottom: 10,
                        color: '#666'
                    }}
                >{l10n.getString('section.colorSelector.themingZones.title')}</div> : null}
                {
                    this.props.showThemingZones ? <ThemingZones
                        activeZone={
                            this.props.type === BackgroundType.Theming ?
                                this.props.themingZone :
                                null
                        }
                        onZoneSelected={v => {
                            this.setState({
                                customColorOpened: false,
                                customColorValue: null,
                                customColorInputValue: null,
                                customColorValidationErrors: []
                            });
                            this.props.onThemingZoneSelected(v);
                        }}
                    /> : null
                }
                <div
                    style={{
                        fontFamily: "open sans",
                        fontSize: '14px',
                        lineHeight: '14px',
                        paddingBottom: 10,
                        color: '#666'
                    }}
                >{l10n.getString('section.colorSelector.swatchList.title')}</div>
                {this.getGroupedPredefinedColors(itemsPerLine).map((colors: string[], lineNumber: number) => {
                    return <div
                        key={"color-line-" + lineNumber}
                        style={autoprefixer({
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            marginBottom: ((lineNumber + 1) * itemsPerLine) >= this.predefinedColors.length ? 0 : 10
                        })}
                    >
                        {colors.map((colorValue: string, colorIndex: number) => {
                            return <div
                                key={"swatch-" + lineNumber + "-" + colorIndex}
                                style={{
                                    display: 'inline-block',
                                    marginRight:  (colorIndex + 1) % itemsPerLine === 0 ? 0 : 8
                                }}
                                onClick={() => {
                                    this.setState({
                                        customColorOpened: false,
                                        customColorValue: null,
                                        customColorInputValue: null,
                                        customColorValidationErrors: []
                                    });
                                    this.onColorSelected(colorValue, ColorType.Swatch);
                                }}
                                data-test-id={"color-swatch-" + lineNumber + "-" + colorIndex}
                            >
                                <ColorSwatchItem
                                    color={colorValue}
                                    checked={this.props.type === BackgroundType.Color &&
                                        ColorUtil.rgb2hex(this.props.color) === colorValue}
                                />
                            </div>;
                        })}
                    </div>;
                })}
            </div>
            {this.renderCustomInput()}
        </div>;
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/section/components/common/ColorSelectorView.tsx
 **/